// Ship 6e — Quick Intake modal.
// Ship 7j — Extended with "Case Context" collapsible section (feedback #744)
// so new intake staff have guided prompts + structured fields for judgment
// status, action type, and agencies involved.
//
// Header-launched (Layout.js) + Ctrl/Cmd+Shift+I shortcut.  Entry form for
// creating a lead from inside the app (phone call, hallway conversation,
// walk-in).  Backend endpoint POST /api/leads is admin OR can_manage_leads
// gated; the caller is responsible for gating the button so the modal
// never opens for users who'd 403.
//
// Success paths:
//   • { duplicate: false, lead_id } → toast "Lead created" + navigate to
//     /leads/:lead_id so the drawer opens on the fresh record.
//   • { duplicate: true, lead_id }  → toast "Existing lead updated" + same
//     navigate — the 30-min dedup fold means the server bumped submission_count
//     on an already-open lead rather than creating a duplicate.
//
// Cmd/Ctrl+Enter submits (via useModal({ onSubmit })).  Esc closes.
//
// Feedback #841 (Shawn, 2026-10-19) — "send the questionnaire to the
// prospective client at the end of each call".  When an email is typed, the
// form offers "Email the intake questionnaire when I save" (UNCHECKED every
// time — never automatic, never remembered).  On save it calls the existing
// POST /api/leads/:id/questionnaire/send with the single active template, or
// the one staff pick when several are active; with none active it says so and
// the box is disabled.  Not offered without an email, and skipped (with a
// visible notice) when the 30-minute dedup fold lands on a lead that is
// already declined or converted.  If the send fails the lead is still saved:
// the modal stays open on a "saved, questionnaire NOT sent" panel with Try
// again / Open lead, so the failure is never silent and never re-creates the
// lead.

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { quickCreateLead, listQuestionnaireTemplates, sendLeadQuestionnaire } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useModal } from '../hooks/useModal'
import {
  JUDGMENT_STATUS_OPTIONS,
  ACTION_TYPE_OPTIONS,
  COMMON_AGENCIES,
  AGENCIES_MAX,
  DETAILS_MAX_CHARS,
} from '../lib/quickIntakeFields'

// Persist the Case Context expand/collapse state per user so a returning
// intake staffer doesn't have to re-open it every time.  Ship 7j: default
// collapsed so new users aren't overwhelmed on first click.
const CASE_CONTEXT_OPEN_KEY = 'lextrack_quick_intake_case_context_open'

// Human labels shown in the datalist → backend enum keys (ck_leads_source).
// Backend accepts: website, referral, google_ads, google, linkedin, social,
// directory, lawyer_directory, walk_in, phone_call, other, unknown.
// Any label not in this map falls through to trim() as-is; unknown
// free-text also gets mapped to 'other' below so we never 400 the user
// for picking a friendly-looking datalist suggestion.
const SOURCE_LABEL_TO_ENUM = {
  'Referral': 'referral',
  'Attorney referral': 'referral',
  'Google Search': 'google',
  'Google Ads': 'google_ads',
  'Website': 'website',
  'Walk-in': 'walk_in',
  'Phone call': 'phone_call',
  'LinkedIn': 'linkedin',
  'Facebook': 'social',
  'Instagram': 'social',
  'Existing client': 'referral',
  'Prior client': 'referral',
  'Court appearance': 'other',
  'Bar association': 'lawyer_directory',
  'Other': 'other',
}
const ENUM_SOURCES = new Set([
  'website', 'referral', 'google_ads', 'google', 'linkedin', 'social',
  'directory', 'lawyer_directory', 'walk_in', 'phone_call', 'other', 'unknown'
])
const SOURCE_SUGGESTIONS = Object.keys(SOURCE_LABEL_TO_ENUM)

// Coerce user input (label OR enum key OR unknown free-text) into a valid
// backend enum value.  Precedence: exact label match → enum passthrough →
// case-insensitive label match → 'other' fallback so intake never fails
// due to a source string.
function coerceSourceToEnum (raw) {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  if (SOURCE_LABEL_TO_ENUM[s]) return SOURCE_LABEL_TO_ENUM[s]
  if (ENUM_SOURCES.has(s.toLowerCase())) return s.toLowerCase()
  const ci = Object.keys(SOURCE_LABEL_TO_ENUM).find(
    k => k.toLowerCase() === s.toLowerCase()
  )
  if (ci) return SOURCE_LABEL_TO_ENUM[ci]
  return 'other'
}

export default function QuickIntakeModal ({ onClose }) {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', source: '', notes: '',
    // Ship 7j — Case Context (feedback #744).  All optional; default to
    // empty strings so uncontrolled → controlled warnings don't fire.
    judgment_status: '',
    judgment_details: '',
    action_type: '',
    action_details: '',
    // agencies_involved is an ARRAY on the wire; tag chips in the UI.
    agencies_involved: [],
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Feedback #841 — "Send questionnaire now".  templates: null = loading,
  // [] = none active, otherwise the active list.  sendQnr starts false on
  // every open (never automatic).  savedLead is set only when the lead saved
  // but the questionnaire send failed — the modal then shows the result panel.
  const [templates, setTemplates] = useState(null)
  const [templatesError, setTemplatesError] = useState('')
  const [sendQnr, setSendQnr] = useState(false)
  const [qnrTemplateId, setQnrTemplateId] = useState('')
  const [savedLead, setSavedLead] = useState(null)   // { leadId, email, duplicate }
  const [qnrError, setQnrError] = useState('')

  useEffect(() => {
    let cancelled = false
    listQuestionnaireTemplates()
      .then(r => {
        if (cancelled) return
        const list = (r?.data?.templates || []).filter(t => t && t.is_active !== false)
        setTemplates(list)
        if (list.length === 1) setQnrTemplateId(String(list[0].id))
      })
      .catch(err => {
        if (cancelled) return
        setTemplates([])
        setTemplatesError(err?.response?.data?.error || 'Could not load questionnaire templates.')
      })
    return () => { cancelled = true }
  }, [])

  // Ship 7j — Case Context expand/collapse state.  Persisted per user in
  // localStorage; default false so new intake staff aren't overwhelmed
  // on first modal open (per Gabe's ask — the section is opt-in guided
  // prompts, not a required form).  try/catch for private-mode / cleared
  // storage resilience.
  const [caseContextOpen, setCaseContextOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(CASE_CONTEXT_OPEN_KEY)
      return raw === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(CASE_CONTEXT_OPEN_KEY, String(caseContextOpen))
    } catch {}
  }, [caseContextOpen])

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  // Agency-tag helpers.  Agencies stored as string[] client-side + on the
  // wire.  Backend caps at 20 (DB CHECK); frontend enforces same cap with
  // friendly warning so user sees it before server 400s.
  const [agencyDraft, setAgencyDraft] = useState('')
  const addAgency = (raw) => {
    const val = String(raw || '').trim()
    if (!val) return
    if (form.agencies_involved.length >= AGENCIES_MAX) {
      setError(`Max ${AGENCIES_MAX} agencies allowed.  Remove one first.`)
      return
    }
    // Case-insensitive dedup so "Sheriff" + "sheriff" don't both land.
    const existing = form.agencies_involved.some(
      a => a.toLowerCase() === val.toLowerCase()
    )
    if (existing) { setAgencyDraft(''); return }
    set('agencies_involved', [...form.agencies_involved, val])
    setAgencyDraft('')
    setError('')
  }
  const removeAgency = (val) => {
    set('agencies_involved', form.agencies_involved.filter(a => a !== val))
  }
  const handleAgencyKeyDown = (e) => {
    // Enter or comma commits the current draft as a tag.
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addAgency(agencyDraft)
    } else if (e.key === 'Backspace' && !agencyDraft && form.agencies_involved.length > 0) {
      // Backspace on empty input removes the last tag (standard chip UX).
      set('agencies_involved', form.agencies_involved.slice(0, -1))
    }
  }

  // Backend allows last_name null (single-name walk-ins).  Modal only
  // requires first_name so we don't block the primary intake UI.
  const canSubmit = form.first_name.trim().length > 0

  // Ship 7j — Show conditional details textarea only when the parent
  // enum is set to a value where "details" is meaningful.  Judgment
  // details only for pending / entered (not none / unknown / n_a).
  // Action details for anything other than none / unknown.
  const showJudgmentDetails = form.judgment_status === 'pending'
    || form.judgment_status === 'entered'
  const showActionDetails = !!form.action_type
    && form.action_type !== 'none'
    && form.action_type !== 'unknown'

  const emailTyped = form.email.trim()
  const hasTemplates = Array.isArray(templates) && templates.length > 0
  const qnrOffered = !!emailTyped

  // Send the questionnaire for a lead that is already saved.  Returns
  // { ok } | { skipped, message } | { failed, message }.  `duplicate` = the
  // 30-minute dedup fold landed on an existing lead (matched by email OR
  // phone), which may already be declined or converted — never send to those
  // — and whose email on file is what the server sends to.  The fold does not
  // update that email, so if it is not the address staff just typed, do not
  // send: the box promised "to <typed email>".
  const sendQuestionnaireFor = async (leadId, { duplicate = false, typedEmail = '' } = {}) => {
    try {
      if (duplicate) {
        const d = (await api.get(`/api/leads/${leadId}`))?.data || {}
        if (d.lead?.converted_client_id) return { skipped: true, message: 'This caller matched an existing lead that is already a client, so no questionnaire was sent.' }
        if (d.decision_chip === 'declined') return { skipped: true, message: 'This caller matched an existing lead that was declined, so no questionnaire was sent.' }
        const onFile = String(d.lead?.email || '').trim().toLowerCase()
        if (!onFile || onFile !== String(typedEmail || '').trim().toLowerCase()) {
          return { skipped: true, message: 'This caller matched an existing lead with a different email on file, so no questionnaire was sent.  Check the lead and send it from its drawer.' }
        }
      }
      await sendLeadQuestionnaire(leadId, { template_id: parseInt(qnrTemplateId, 10) })
      return { ok: true }
    } catch (err) {
      const status = err?.response?.status
      const d = err?.response?.data || {}
      if (status === 409 && d.active_questionnaire_id) {
        return { skipped: true, message: 'This lead already has a questionnaire out, so a second one was not sent.' }
      }
      return { failed: true, message: d.error || d.message || 'The questionnaire could not be sent.' }
    }
  }

  const retrySend = async () => {
    if (!savedLead || saving) return
    setSaving(true); setQnrError('')
    try {
      const out = await sendQuestionnaireFor(savedLead.leadId, { duplicate: savedLead.duplicate, typedEmail: savedLead.email })
      if (out.failed) { setQnrError(out.message); return }
      showToast(out.ok ? `Questionnaire sent to ${savedLead.email}` : out.message, out.ok ? 'success' : 'warning')
      onClose?.()
      navigate(`/leads/${savedLead.leadId}`)
    } finally { setSaving(false) }
  }

  const openSavedLead = () => {
    const id = savedLead?.leadId
    onClose?.()
    navigate(id ? `/leads/${id}` : '/leads')
  }

  const submit = async () => {
    if (savedLead) { retrySend(); return }
    if (!canSubmit || saving) return
    const wantQnr = sendQnr && qnrOffered && hasTemplates
    if (wantQnr && !qnrTemplateId) { setError('Choose which questionnaire to send, or untick “Email the intake questionnaire”.'); return }
    setSaving(true); setError('')
    try {
      const body = {
        first_name: form.first_name.trim(),
      }
      if (form.last_name.trim()) body.last_name = form.last_name.trim()
      if (form.email.trim()) body.email = form.email.trim()
      if (form.phone.trim()) body.phone = form.phone.trim()
      const sourceEnum = coerceSourceToEnum(form.source)
      if (sourceEnum) body.source = sourceEnum
      if (form.notes.trim()) body.notes = form.notes.trim()

      // Ship 7j — Case Context fields.  Only send when set (backend
      // treats absence as "leave NULL" via touched-flag).  Enum 'unknown'
      // is a legit user choice — send it so the audit trail records
      // that intake staff asked and didn't get an answer, distinct
      // from "never asked" which is NULL.
      if (form.judgment_status) body.judgment_status = form.judgment_status
      if (form.judgment_details.trim()) body.judgment_details = form.judgment_details.trim()
      if (form.action_type) body.action_type = form.action_type
      if (form.action_details.trim()) body.action_details = form.action_details.trim()
      if (form.agencies_involved.length > 0) body.agencies_involved = form.agencies_involved

      const r = await quickCreateLead(body)
      const leadId = r.data?.lead_id || r.data?.id
      const duplicate = r.data?.duplicate === true
      const savedMsg = duplicate ? 'Existing lead updated (30-min dedup)' : 'Lead created'

      if (wantQnr && leadId && body.email) {
        const out = await sendQuestionnaireFor(leadId, { duplicate, typedEmail: body.email })
        if (out.failed) {
          // Lead IS saved; keep the modal open on the result panel so the
          // failure is visible and a retry cannot create a second lead.
          setSavedLead({ leadId, email: body.email, duplicate })
          setQnrError(out.message)
          return
        }
        if (out.skipped) showToast(`${savedMsg} — ${out.message}`, 'warning')
        else showToast(`${savedMsg} — questionnaire sent to ${body.email}`, 'success')
      } else {
        showToast(savedMsg, duplicate ? 'warning' : 'success')
      }
      onClose?.()
      if (leadId) navigate(`/leads/${leadId}`)
      else navigate('/leads')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create lead')
    } finally { setSaving(false) }
  }

  const handleFormSubmit = (e) => { e.preventDefault(); submit() }

  const { containerProps } = useModal({ onClose, onSubmit: submit })

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose?.() }}
    >
      <div
        {...containerProps}
        aria-labelledby="quick-intake-heading"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-3 flex items-center justify-between flex-shrink-0">
          <h3 id="quick-intake-heading" className="text-white font-semibold text-base">
            ⚡ Quick Intake
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-primary-100 hover:text-white text-xl leading-none disabled:opacity-40"
            aria-label="Close"
          >×</button>
        </div>

        {savedLead ? (
          // Feedback #841 — the lead saved but the questionnaire did not.
          // Visible, retryable, and a retry only re-sends (never re-creates).
          <div className="p-5 space-y-4 overflow-y-auto flex-1" data-testid="qi-saved-panel">
            <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
              <div className="font-semibold">The lead was saved, but the questionnaire was NOT sent.</div>
              {qnrError && <div className="mt-1">{qnrError}</div>}
            </div>
            <p className="text-xs text-gray-600">
              Try again now, or open the lead and use <strong>📝 Intake questionnaire → + Send questionnaire</strong> there.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={openSavedLead}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
              >Open lead</button>
              <button
                type="button"
                onClick={retrySend}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-40"
              >{saving ? 'Sending…' : 'Try sending again'}</button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleFormSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-xs text-gray-500">
            Fast entry for phone calls, walk-ins, or hallway conversations.
            The lead lands in your pipeline immediately + all your usual
            triggers fire (autoresponder, conflict check, drip enrollment).
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm" role="alert">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="qi-first" className="block text-xs font-medium text-gray-700 mb-1">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                id="qi-first"
                data-autofocus
                type="text"
                required
                maxLength={120}
                value={form.first_name}
                onChange={(e) => set('first_name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="qi-last" className="block text-xs font-medium text-gray-700 mb-1">
                Last name <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="qi-last"
                type="text"
                maxLength={120}
                value={form.last_name}
                onChange={(e) => set('last_name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="qi-email" className="block text-xs font-medium text-gray-700 mb-1">Email</label>
              <input
                id="qi-email"
                type="email"
                maxLength={200}
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="qi-phone" className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
              <input
                id="qi-phone"
                type="tel"
                maxLength={40}
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="qi-source" className="block text-xs font-medium text-gray-700 mb-1">Source</label>
            <input
              id="qi-source"
              type="text"
              list="qi-source-list"
              maxLength={80}
              value={form.source}
              onChange={(e) => set('source', e.target.value)}
              placeholder="e.g. Google Search, Referral, Walk-in"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <datalist id="qi-source-list">
              {SOURCE_SUGGESTIONS.filter(Boolean).map(s => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div>
            <label htmlFor="qi-notes" className="block text-xs font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400 font-normal">(what did they say?)</span>
            </label>
            <textarea
              id="qi-notes"
              rows={3}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Slip-and-fall at grocery store, wants consultation this week…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">
              {form.notes.length}/2000
            </p>
          </div>

          {/* Ship 7j — Case Context (feedback #744).  Collapsible so new
              intake staff aren't overwhelmed on first open; every field
              carries a visible italic prompt so staff know exactly what
              question to ask the caller. */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setCaseContextOpen(o => !o)}
              aria-expanded={caseContextOpen}
              aria-controls="qi-case-context-panel"
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition text-left"
            >
              <span className="text-xs font-medium text-gray-700">
                📋 Case Context
                <span className="ml-2 text-gray-400 font-normal">
                  {caseContextOpen ? '(click to collapse)' : '(guided prompts — click to expand)'}
                </span>
              </span>
              <span className="text-gray-500 text-lg leading-none">
                {caseContextOpen ? '−' : '+'}
              </span>
            </button>

            {caseContextOpen && (
              <div id="qi-case-context-panel" className="p-3 space-y-4 bg-white border-t border-gray-200">
                {/* Judgment status */}
                <fieldset>
                  <legend className="text-xs font-medium text-gray-700 mb-1">
                    Judgment status <span className="text-gray-400 font-normal">(optional)</span>
                  </legend>
                  <div className="flex flex-wrap gap-2 mb-1" role="radiogroup" aria-describedby="qi-judgment-status-hint">
                    {JUDGMENT_STATUS_OPTIONS.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded border cursor-pointer transition ${form.judgment_status === opt.value ? 'bg-primary-50 border-primary-500 text-primary-800' : 'bg-white border-gray-300 hover:border-primary-300'}`}
                      >
                        <input
                          type="radio"
                          name="qi-judgment-status"
                          value={opt.value}
                          checked={form.judgment_status === opt.value}
                          onChange={() => set('judgment_status', opt.value)}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                    {form.judgment_status && (
                      <button
                        type="button"
                        onClick={() => set('judgment_status', '')}
                        className="text-[10px] text-gray-400 hover:text-gray-600 underline self-center"
                        aria-label="Clear judgment status"
                      >clear</button>
                    )}
                  </div>
                  <p id="qi-judgment-status-hint" className="text-[11px] italic text-gray-500 leading-snug">
                    💡 {form.judgment_status
                      ? JUDGMENT_STATUS_OPTIONS.find(o => o.value === form.judgment_status)?.tooltip
                      : 'Ask the caller: "Is there already a judgment entered against you?"'}
                  </p>
                </fieldset>

                {showJudgmentDetails && (
                  <div>
                    <label htmlFor="qi-judgment-details" className="block text-xs font-medium text-gray-700 mb-1">
                      Judgment details
                    </label>
                    <textarea
                      id="qi-judgment-details"
                      rows={3}
                      maxLength={DETAILS_MAX_CHARS}
                      value={form.judgment_details}
                      onChange={(e) => set('judgment_details', e.target.value)}
                      placeholder='e.g. "Judgment entered 2024-05-15 in LASC case #24STCV12345 for $42,000"'
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-describedby="qi-judgment-details-hint"
                    />
                    <p id="qi-judgment-details-hint" className="text-[10px] text-gray-400 mt-0.5">
                      {form.judgment_details.length}/{DETAILS_MAX_CHARS} · Include case number, date, amount if the caller has it.
                    </p>
                  </div>
                )}

                {/* Action type */}
                <fieldset>
                  <legend className="text-xs font-medium text-gray-700 mb-1">
                    Action type <span className="text-gray-400 font-normal">(optional)</span>
                  </legend>
                  <div className="flex flex-wrap gap-2 mb-1" role="radiogroup" aria-describedby="qi-action-type-hint">
                    {ACTION_TYPE_OPTIONS.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded border cursor-pointer transition ${form.action_type === opt.value ? 'bg-primary-50 border-primary-500 text-primary-800' : 'bg-white border-gray-300 hover:border-primary-300'}`}
                      >
                        <input
                          type="radio"
                          name="qi-action-type"
                          value={opt.value}
                          checked={form.action_type === opt.value}
                          onChange={() => set('action_type', opt.value)}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                    {form.action_type && (
                      <button
                        type="button"
                        onClick={() => set('action_type', '')}
                        className="text-[10px] text-gray-400 hover:text-gray-600 underline self-center"
                        aria-label="Clear action type"
                      >clear</button>
                    )}
                  </div>
                  <p id="qi-action-type-hint" className="text-[11px] italic text-gray-500 leading-snug">
                    💡 {form.action_type
                      ? ACTION_TYPE_OPTIONS.find(o => o.value === form.action_type)?.tooltip
                      : 'Ask: "What kind of action has been filed? Are you suing someone or being sued? Criminal charges? An agency notice?"'}
                  </p>
                </fieldset>

                {showActionDetails && (
                  <div>
                    <label htmlFor="qi-action-details" className="block text-xs font-medium text-gray-700 mb-1">
                      Action details
                    </label>
                    <textarea
                      id="qi-action-details"
                      rows={3}
                      maxLength={DETAILS_MAX_CHARS}
                      value={form.action_details}
                      onChange={(e) => set('action_details', e.target.value)}
                      placeholder='e.g. "Sued for breach of contract, complaint filed by Acme Corp on 3/12, case #24STCV6789 in LASC"'
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-describedby="qi-action-details-hint"
                    />
                    <p id="qi-action-details-hint" className="text-[10px] text-gray-400 mt-0.5">
                      {form.action_details.length}/{DETAILS_MAX_CHARS} · Include opposing party, court, case number, deadline if known.
                    </p>
                  </div>
                )}

                {/* Agencies involved */}
                <div>
                  <label htmlFor="qi-agency-input" className="block text-xs font-medium text-gray-700 mb-1">
                    Agencies involved <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <p className="text-[11px] italic text-gray-500 leading-snug mb-2" id="qi-agencies-hint">
                    💡 Ask: "Which police department, sheriff's office, county, or agency was involved?"  Add each one — press Enter or comma between tags.
                  </p>
                  {form.agencies_involved.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {form.agencies_involved.map(a => (
                        <span
                          key={a}
                          className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs px-2 py-0.5 rounded-full"
                        >
                          {a}
                          <button
                            type="button"
                            onClick={() => removeAgency(a)}
                            className="text-indigo-500 hover:text-indigo-900 text-sm leading-none"
                            aria-label={`Remove ${a}`}
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    id="qi-agency-input"
                    type="text"
                    list="qi-agency-list"
                    value={agencyDraft}
                    onChange={(e) => setAgencyDraft(e.target.value)}
                    onKeyDown={handleAgencyKeyDown}
                    onBlur={() => agencyDraft.trim() && addAgency(agencyDraft)}
                    placeholder={form.agencies_involved.length >= AGENCIES_MAX
                      ? `Max ${AGENCIES_MAX} reached`
                      : 'Type an agency name and press Enter…'}
                    disabled={form.agencies_involved.length >= AGENCIES_MAX}
                    maxLength={100}
                    aria-describedby="qi-agencies-hint qi-agencies-count"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 disabled:bg-gray-50"
                  />
                  <datalist id="qi-agency-list">
                    {COMMON_AGENCIES.map(a => <option key={a} value={a} />)}
                  </datalist>
                  <p id="qi-agencies-count" className="text-[10px] text-gray-400 mt-0.5">
                    {form.agencies_involved.length}/{AGENCIES_MAX} agencies
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* #815 — hand-off to the intake script.  This modal stays the FAST
              capture: get the caller's name and number recorded before they
              hang up.  The guided interview — county, type of case, the facts
              by who/what/why/where/when/how, and the CPS branch — lives on the
              lead itself, because those columns are written through
              PUT /api/leads/:id/intake-script and need a lead that exists.
              Saving here navigates straight to that drawer, so the staffer
              keeps going without losing the call.  Telling them that up front
              stops the modal reading like the whole checklist is missing. */}
          {/* Feedback #841 — "Send questionnaire now".  Offered only once an
              email is typed; unchecked on every open (never automatic). */}
          {qnrOffered && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 space-y-2" data-testid="qi-qnr-box">
              {templates === null ? (
                <p className="text-[11px] text-gray-500">Checking for a questionnaire template…</p>
              ) : !hasTemplates ? (
                <p className="text-[11px] text-amber-800" role="note">
                  {templatesError
                    ? `The intake questionnaire can't be offered here: ${templatesError}`
                    : 'No active questionnaire template, so the intake questionnaire can’t be sent yet.  An admin can build one on Intake Forms → Questionnaire templates.'}
                </p>
              ) : (
                <>
                  <label className="flex items-start gap-2 text-xs text-gray-800 cursor-pointer">
                    <input
                      id="qi-send-qnr"
                      type="checkbox"
                      checked={sendQnr}
                      onChange={(e) => { setSendQnr(e.target.checked); setError('') }}
                      className="mt-0.5"
                    />
                    <span>
                      <strong>📝 Email the intake questionnaire to {emailTyped}</strong> when I save
                      <span className="block text-[11px] text-gray-600 mt-0.5">
                        One email with a private link that works for 14 days.  If it isn&rsquo;t
                        returned by then, the lead is labelled &ldquo;Questionnaire not returned &mdash;
                        send non-engagement letter&rdquo;.
                      </span>
                    </span>
                  </label>
                  {sendQnr && templates.length > 1 && (
                    <div>
                      <label htmlFor="qi-qnr-template" className="block text-[11px] font-medium text-gray-700 mb-1">Which questionnaire?</label>
                      <select
                        id="qi-qnr-template"
                        value={qnrTemplateId}
                        onChange={(e) => { setQnrTemplateId(e.target.value); setError('') }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                      >
                        <option value="">Choose a questionnaire…</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                  {sendQnr && templates.length === 1 && (
                    <p className="text-[11px] text-gray-600">Questionnaire: <strong>{templates[0].name}</strong></p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
            <p className="text-[11px] text-indigo-900">
              <strong>Next:</strong> saving opens this lead&rsquo;s drawer, where the
              <strong> 📞 Intake script</strong> walks you through location, type of case,
              the facts (who / what / why / where / when / how), and the CPS questions.
              Capture the name and number here first so you have a way to call back.
            </p>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <span className="text-[10px] text-gray-400">
              ⌘/Ctrl+Enter to save · Esc to close
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
              >Cancel</button>
              <button
                type="submit"
                disabled={!canSubmit || saving}
                className="px-4 py-2 text-sm rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-40"
              >{saving ? 'Saving…' : (sendQnr && qnrOffered && hasTemplates ? 'Save & send questionnaire' : 'Save Lead')}</button>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}
